/**
 * Game plan (designs/Caged Game Plan, docs/game-design.md §8, D5): how the player's fighter fights
 * when they're away: style, aggression, a plan per round and what to do when hurt, with the
 * corner's read of it. Saved on the game server; it applies from the next league fight.
 */
import { aggressionLabel, cornersRead, DEFAULT_PLAN, FIGHT, type GamePlan, type PlanStyle, type RoundPlan, type WhenHurt } from '@caged/shared';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLeague } from '../../state/league';
import { useApp } from '../../state/store';
import { ArenaBackdrop } from '../components/Backdrop';
import { DesktopHeader, MenuButton } from '../components/Chrome';
import { FighterArt } from '../components/FighterArt';
import ui from '../components/ui.module.css';
import { useLayoutMode, useMyFighter, useMyLook, useNow, useScrollTop } from '../hooks';
import { FightChip, useClockTime } from '../league';
import '../ui.css';
import s from './GamePlanScreen.module.css';

const STYLES: { id: PlanStyle; icon: string }[] = [
  { id: 'pressure', icon: '⇒' },
  { id: 'counter', icon: '↺' },
  { id: 'grinder', icon: '⊞' },
];
const ROUND_PLANS: readonly RoundPlan[] = ['feelOut', 'push', 'finish'];
const HURT: readonly WhenHurt[] = ['cover', 'swingBack'];

export function GamePlanScreen() {
  const { t } = useTranslation();
  const layout = useLayoutMode();
  const leagueBack = useApp((st) => st.leagueBack);
  const name = useApp((st) => st.settings.playerName);
  const fighterId = useMyFighter();
  const myLook = useMyLook();
  const ranks = useApp((st) => st.progress.ranks);
  const mine = useLeague((st) => st.mine);
  const load = useLeague((st) => st.load);
  const savePlan = useLeague((st) => st.savePlan);
  const time = useClockTime();
  const now = useNow(30_000);
  const own = mine?.status === 'ok' ? mine : null;
  const [plan, setPlan] = useState<GamePlan>(own?.plan ?? DEFAULT_PLAN);
  const [saved, setSaved] = useState(!!own);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const sliderId = useId();
  useScrollTop();

  useEffect(() => {
    void load();
  }, [load]);
  // The saved plan arrives with the league.
  useEffect(() => {
    if (own) {
      setPlan(own.plan);
      setSaved(true);
    }
    // Only when the league first loads, not on every refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!own]);

  const change = (patch: Partial<GamePlan>) => {
    setPlan((p) => ({ ...p, ...patch }));
    setSaved(false);
    setFailed(false);
  };
  const lockIn = async () => {
    setBusy(true);
    const ok = await savePlan(plan);
    setBusy(false);
    setSaved(ok);
    setFailed(!ok);
  };
  const read = cornersRead(plan, ranks);
  const canSave = mine?.status === 'ok';

  const content = (
    <div className={s.stack}>
      <section className={s.group} aria-labelledby="plan-style">
        <h2 id="plan-style" className={[ui.label, s.groupTitle].join(' ')}>
          {t('screens.plan.style')}
        </h2>
        <div className={s.styles} role="radiogroup" aria-labelledby="plan-style">
          {STYLES.map((x) => (
            <button
              key={x.id}
              type="button"
              role="radio"
              aria-checked={plan.style === x.id}
              className={[s.styleCard, plan.style === x.id ? s.on : ''].join(' ')}
              onClick={() => change({ style: x.id })}
            >
              <span className={s.styleIcon} aria-hidden="true">
                {x.icon}
              </span>
              <span className={s.styleName}>{t(`screens.plan.styles.${x.id}.name`)}</span>
              <span className={s.styleDesc}>{t(`screens.plan.styles.${x.id}.desc`)}</span>
            </button>
          ))}
        </div>
      </section>

      <section className={[ui.card, s.card].join(' ')}>
        <div className={s.cardHead}>
          <label htmlFor={sliderId} className={ui.label}>
            {t('screens.plan.aggression')}
          </label>
          <span className={s.value}>{t(`screens.plan.aggressionLabels.${aggressionLabel(plan.aggression)}`)}</span>
        </div>
        <input
          id={sliderId}
          className={s.slider}
          type="range"
          min={0}
          max={100}
          value={plan.aggression}
          onChange={(e) => change({ aggression: Number(e.target.value) })}
          aria-valuetext={t(`screens.plan.aggressionLabels.${aggressionLabel(plan.aggression)}`)}
        />
        <div className={s.ends} aria-hidden="true">
          <span>{t('screens.plan.aggressionLabels.patient')}</span>
          <span>{t('screens.plan.aggressionLabels.reckless')}</span>
        </div>
      </section>

      <section className={s.group} aria-labelledby="plan-rounds">
        <h2 id="plan-rounds" className={[ui.label, s.groupTitle].join(' ')}>
          {t('screens.plan.rounds')}
        </h2>
        {Array.from({ length: FIGHT.rounds }, (_, i) => (
          <div key={i} className={s.round} role="radiogroup" aria-label={t('screens.plan.roundN', { n: i + 1 })}>
            <span className={s.roundN} aria-hidden="true">
              {t('screens.plan.roundN', { n: i + 1 })}
            </span>
            <div className={s.segment}>
              {ROUND_PLANS.map((r) => (
                <button
                  key={r}
                  type="button"
                  role="radio"
                  aria-checked={plan.rounds[i] === r}
                  className={[s.segmentBtn, plan.rounds[i] === r ? s.segmentOn : ''].join(' ')}
                  onClick={() => change({ rounds: plan.rounds.map((x, j) => (j === i ? r : x)) })}
                >
                  {t(`screens.plan.roundPlans.${r}`)}
                </button>
              ))}
            </div>
          </div>
        ))}
      </section>

      <section className={s.group} aria-labelledby="plan-hurt">
        <h2 id="plan-hurt" className={[ui.label, s.groupTitle].join(' ')}>
          {t('screens.plan.whenHurt')}
        </h2>
        <div className={s.hurt} role="radiogroup" aria-labelledby="plan-hurt">
          {HURT.map((h) => (
            <button
              key={h}
              type="button"
              role="radio"
              aria-checked={plan.whenHurt === h}
              className={[s.hurtCard, plan.whenHurt === h ? s.on : ''].join(' ')}
              onClick={() => change({ whenHurt: h })}
            >
              <span className={s.styleName}>{t(`screens.plan.hurt.${h}.name`)}</span>
              <span className={s.styleDesc}>{t(`screens.plan.hurt.${h}.desc`)}</span>
            </button>
          ))}
        </div>
      </section>

      <section className={s.read} aria-labelledby="plan-read">
        <div className={s.cardHead}>
          <h2 id="plan-read" className={ui.label}>
            {t('screens.plan.read')}
          </h2>
          <span className={s.value}>{t(`screens.plan.names.${read.name}`)}</span>
        </div>
        {(
          [
            ['finish', read.finish, 'var(--amber)'],
            ['gasTank', read.gasTank, 'var(--win)'],
            ['chinExposed', read.chinExposed, 'var(--loss)'],
          ] as const
        ).map(([key, v, color]) => (
          <div key={key} className={s.stat}>
            <span className={s.statName}>{t(`screens.plan.stats.${key}`)}</span>
            <span className={s.statTrack} aria-hidden="true">
              <span className={s.statFill} style={{ width: `${v}%`, background: color }} />
            </span>
            <span className={s.statValue}>{v}%</span>
          </div>
        ))}
        <p className={s.readout}>{t(`screens.plan.readouts.${read.readout}`)}</p>
      </section>

      <div className={s.save}>
        <button
          type="button"
          className={[saved ? s.lockedBtn : ui.primary, s.saveBtn].join(' ')}
          onClick={() => void lockIn()}
          disabled={!canSave || busy || saved}
        >
          {saved ? t('screens.plan.locked') : t('screens.plan.lockIn')}
          <span className={ui.arrow} aria-hidden="true">
            {saved ? '✓' : '→'}
          </span>
        </button>
        {failed && (
          <p className={s.error} role="alert">
            {t('common.saveFailed')}
          </p>
        )}
        <p className={s.applies}>
          {own ? t('screens.plan.applies', { time: time(own.next.fightAt) }) : mine?.status === 'guest' ? t('screens.plan.guest') : ''}
        </p>
        <p className={s.used}>{t('screens.plan.used')}</p>
      </div>
    </div>
  );

  const hero = (
    <div className={s.hero}>
      <div className={s.heroText}>
        <h1 className={s.title}>{t('screens.plan.title')}</h1>
        <p className={s.intro}>{t('screens.plan.intro', { name: name || t('screens.home.yourFighter') })}</p>
      </div>
      <FighterArt id={fighterId} height={96} look={myLook} />
    </div>
  );

  if (layout === 'desktop') {
    return (
      <div className={['cg-ui', ui.screen].join(' ')}>
        <DesktopHeader />
        <div className={s.desk}>
          <div className={s.deskTop}>
            <button type="button" className={s.backLink} onClick={leagueBack}>
              ← {t('common.back')}
            </button>
            {own && <FightChip next={own.next} now={now} />}
          </div>
          {hero}
          {content}
        </div>
      </div>
    );
  }
  return (
    <div className={['cg-ui', ui.screen].join(' ')}>
      <div className={s.phoneBg} aria-hidden="true">
        <ArenaBackdrop tone="menu" shade="linear-gradient(180deg,rgba(20,19,18,.8),rgba(20,19,18,.3) 45%,#141312)" />
      </div>
      <div className={ui.column}>
        <div className={s.topBar}>
          <button type="button" className={ui.iconBtn} onClick={leagueBack} aria-label={t('common.back')}>
            <span aria-hidden="true">←</span>
          </button>
          {own && <FightChip next={own.next} now={now} compact />}
          <MenuButton />
        </div>
        {hero}
        {content}
      </div>
    </div>
  );
}
